//dashboard.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import useIdleTimer from "../../utils/useIdleTimer";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import {
  fetchUserStorageUsage,
  fetchUserCanvases,
  fetchUserAssets,
  removeCanvas,
  removeAsset,
  unlinkAssetFromAllCanvases,
  upsertAsset,
  renameCanvas,
  renameAsset,
  duplicateCanvas,
  uploadFileToStorage,
  getStorageLimitBytes,
  truncateName,
  computeFileHash,
  deleteCanvas,
  deleteAsset,
  bulkDeleteCanvases,
  bulkDeleteAssets,
  updateUserName,
} from "../../services/dashboardService";
import { db } from "../../config/firebase";
import { doc, getDoc } from "firebase/firestore";
import "./Dashboard.css";

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Idle timer hook - 1 hour
  useIdleTimer(3600000);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("Recent Files");
  const [showNestedList, setShowNestedList] = useState(false);
  const [showAssetsNestedList, setShowAssetsNestedList] = useState(false);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [savedStates, setSavedStates] = useState([]);
  const [recentFiles, setRecentFiles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [assetCanvases, setAssetCanvases] = useState([]);
  const [isCanvasDeleteModalOpen, setIsCanvasDeleteModalOpen] = useState(false);
  const [canvasToDelete, setCanvasToDelete] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showStorageLimitModal, setShowStorageLimitModal] = useState(false);
  const [showCanvasLimitModal, setShowCanvasLimitModal] = useState(false);
  const [currentUsageBytes, setCurrentUsageBytes] = useState(0);
  const [blockedFileSize, setBlockedFileSize] = useState(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [selectedCanvases, setSelectedCanvases] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [editingName, setEditingName] = useState(null);
  const [editingValue, setEditingValue] = useState(""); // Current input value
  const [originalName, setOriginalName] = useState(""); // Store original name during editing

  const [isTrialSubscription, setIsTrialSubscription] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(null);

  // Name editing state for account modal
  const [isEditingName, setIsEditingName] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [nameUpdateMessage, setNameUpdateMessage] = useState('');

  const hiddenCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Firebase user setup with retry mechanism
  useEffect(() => {
    const fetchUserDisplayName = async (retryCount = 0) => {
      if (currentUser) {
        try {
          // Get display name from the new hierarchical structure: users/{userId}/subscription/profile
          const profileDoc = await getDoc(
            doc(db, "users", currentUser.uid, "subscription", "profile"),
          );
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();


            // Use firstName directly - that's what they entered in the signup form
            const fetchedName = profileData.firstName || "User";
            setUserName(fetchedName);
            setNewUserName(fetchedName); // Initialize editing state with current name
          } else {


            // Retry up to 3 times with delay for new users
            if (retryCount < 3) {
              setTimeout(() => {
                fetchUserDisplayName(retryCount + 1);
              }, 1000); // Wait 1 second before retry
              return;
            }

            // Final fallback after retries
            setUserName("User");
          }
          setUserEmail(currentUser.email || "");
        } catch (error) {

          setUserName("User");
          setUserEmail(currentUser.email || "");
        }
      }
    };

    fetchUserDisplayName();
  }, [currentUser]);

  // Name change handler functions
  const handleNameChangeSubmit = async () => {
    if (newUserName.trim() === '' || newUserName === userName) {
      setIsEditingName(false); // Cancel if name is empty or unchanged
      return;
    }
    try {
      await updateUserName(currentUser.uid, newUserName);
      setUserName(newUserName); // Update the name in the UI immediately
      setIsEditingName(false); // Exit editing mode
      setNameUpdateMessage("Name updated successfully!");
      setTimeout(() => setNameUpdateMessage(""), 3000); // Clear the message after 3 seconds
    } catch (error) {

      // Optionally, set an error message to display to the user
    }
  };

  const handleEditName = () => {
    setIsEditingName(true);
    setNewUserName(userName); // Ensure the input starts with the current name
    setNameUpdateMessage(""); // Clear any previous success messages
  };

  // 3D Model Screenshot Generation - Restored from original implementation
  const generateModelSnapshot = async (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error("No file provided for snapshot generation"));
        return;
      }

      const loader = new GLTFLoader();
      const url = URL.createObjectURL(file);

      loader.load(
        url,
        (gltf) => {
          // Create a temporary canvas for WebGL rendering
          const tempCanvas = document.createElement("canvas");
          tempCanvas.width = 1080;
          tempCanvas.height = 1080;

          const renderer = new THREE.WebGLRenderer({
            canvas: tempCanvas,
            alpha: true,
            preserveDrawingBuffer: true,
            antialias: true,
          });
          renderer.setPixelRatio(window.devicePixelRatio || 1);
          renderer.setSize(tempCanvas.width, tempCanvas.height, false);
          renderer.toneMapping = THREE.ACESFilmicToneMapping;
          renderer.toneMappingExposure = 10;

          const scene = new THREE.Scene();
          scene.background = new THREE.Color(0xffffff);

          const camera = new THREE.PerspectiveCamera(
            45,
            tempCanvas.width / tempCanvas.height,
            0.1,
            1000,
          );
          camera.position.set(0, 0, 3.5);

          // Add lights
          scene.add(new THREE.AmbientLight(0xffffff, 0.3));
          scene.add(new THREE.HemisphereLight("#ffffff", "#444444", 0.8));
          const spot = new THREE.SpotLight("#ffffff", 1);
          spot.position.set(10, 10, 10);
          spot.angle = 0.3;
          spot.penumbra = 0.5;
          spot.castShadow = true;
          scene.add(spot);
          scene.add(new THREE.PointLight("#ffffff", 1));

          // Add the model
          const root = gltf.scene;
          scene.add(root);
          const box = new THREE.Box3().setFromObject(root);
          const size = box.getSize(new THREE.Vector3());
          const center = box.getCenter(new THREE.Vector3());

          // Center and scale the model
          root.position.x -= center.x;
          root.position.y -= center.y;
          root.position.z -= center.z;
          const maxDim = Math.max(size.x, size.y, size.z);
          const scaleFactor = 2 / maxDim;
          root.scale.set(scaleFactor, scaleFactor, scaleFactor);

          root.traverse((child) => {
            if (child.isMesh) {
              child.material.metalness = 0.8;
              child.material.roughness = 0.2;
            }
          });

          // Render the scene
          requestAnimationFrame(() => {
            renderer.render(scene, camera);
            requestAnimationFrame(() => {
              renderer.render(scene, camera);
              // Get the WebP data URL
              const dataUrl = tempCanvas.toDataURL("image/webp", 0.9);
              // Clean up
              scene.remove(root);
              renderer.dispose();
              URL.revokeObjectURL(url);

              // Convert data URL to blob
              const blob = dataURLtoBlob(dataUrl);
              resolve(blob);
            });
          });
        },
        undefined,
        (error) => {
          URL.revokeObjectURL(url);
          reject(error);
        },
      );
    });
  };

  // Helper function to convert data URL to blob
  function dataURLtoBlob(dataurl) {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  const toggleSelection = (id, itemType) => {
    const setFunction =
      itemType === "canvas" ? setSelectedCanvases : setSelectedAssets;
    setFunction((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleBulkDelete = () => {
    if (selectedCanvases.length > 0 || selectedAssets.length > 0) {
      setIsBulkDeleteModalOpen(true);
    }
  };

  // Dropdown management
  const toggleDropdownMenu = (itemId, type) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [`${type}_${itemId}`]: !prev[`${type}_${itemId}`],
    }));
  };

  const closeAllDropdowns = () => {
    setOpenDropdowns({});
  };

  // Helper functions for improved rename UX
  const startEditing = (id, currentName) => {
    setEditingName(id);
    setEditingValue(currentName);
    setOriginalName(currentName);
  };

  const handleRenameSubmit = async (canvasId, newName) => {
    const trimmedName = newName.trim();

    // If blank or unchanged, just cancel editing
    if (!trimmedName || trimmedName === originalName) {
      setEditingName(null);
      setEditingValue("");
      return;
    }

    try {
      await renameCanvas(canvasId, trimmedName, currentUser.uid);

      await fetchSavedStates();
    } catch (error) {

    } finally {
      setEditingName(null);
      setEditingValue("");
    }
  };

  const handleDuplicateCanvas = async (canvas) => {
    try {


      // Pass both canvas ID and user ID to the duplicate function
      const result = await duplicateCanvas(canvas.id, currentUser.uid);

      if (result) {


        // Refresh the canvas data to show the new duplicate
        await fetchSavedStates();

        closeAllDropdowns();
      }
    } catch (error) {


      // Check if it's a canvas limit error
      if (error.message && error.message.includes("Canvas limit reached")) {
        setShowCanvasLimitModal(true);
      }

      closeAllDropdowns();
    }
  };

  const handleCanvasDeleteDialog = (canvas) => {

    setCanvasToDelete(canvas);
    setIsCanvasDeleteModalOpen(true);

    closeAllDropdowns();
  };

  const handleAssetDeleteDialog = async (asset) => {
    try {
      if (!currentUser?.uid) {

        return;
      }



      // Fetch all canvases for the current user
      const canvases = await fetchUserCanvases(currentUser.uid);

      // Filter canvases to find where the asset is used
      const usedOnCanvases = canvases.filter((canvas) => {
        const isInBgFiles = canvas.bgFiles?.some(
          (f) => f.sourceAssetId === asset.id || f.id === asset.id,
        );
        const isInModelFiles = canvas.modelFiles?.some(
          (f) => f.sourceAssetId === asset.id || f.id === asset.id,
        );
        return isInBgFiles || isInModelFiles;
      });



      // Set state for the modal
      setAssetCanvases(usedOnCanvases);
      setAssetToDelete(asset);
      setIsDeleteModalOpen(true);

      closeAllDropdowns();
    } catch (error) {

      // Still show a basic delete modal on error
      setAssetToDelete(asset);
      setIsDeleteModalOpen(true);
      closeAllDropdowns();
    }
  };

  const confirmBulkDelete = async () => {
    try {
      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }

      // Delete selected canvases using comprehensive delete
      if (selectedCanvases.length > 0) {
        await bulkDeleteCanvases(selectedCanvases, currentUser.uid);
        setSavedStates((prev) =>
          prev.filter((item) => !selectedCanvases.includes(item.id)),
        );
        setRecentFiles((prev) =>
          prev.filter((item) => !selectedCanvases.includes(item.id)),
        );
      }

      // Delete selected assets using comprehensive delete
      if (selectedAssets.length > 0) {
        await bulkDeleteAssets(selectedAssets, currentUser.uid);
        setAssets((prev) =>
          prev.filter((item) => !selectedAssets.includes(item.id)),
        );
      }

      setSelectedCanvases([]);
      setSelectedAssets([]);
      setIsMultiSelectMode(false);
      setIsBulkDeleteModalOpen(false);
    } catch (error) {

      setIsBulkDeleteModalOpen(false);
    }
  };

  // Load saved states and assets
  const fetchSavedStates = async () => {
    if (!currentUser) {

      return;
    }

    try {

      const canvases = await fetchUserCanvases(currentUser.uid);

      const mappedCanvases = canvases.map((canvas) => {
        // Check if mainSnapshotPath exists and is not null
        const snapshotUrl = canvas.mainSnapshotPath
          ? // If it exists, construct a full, servable URL using your backend proxy
            `/api/serve-image?filename=${encodeURIComponent(canvas.mainSnapshotPath)}`
          : // Otherwise, set it to null so no image is displayed
            null;

        return {
          ...canvas,
          // Use the newly constructed URL for the snapshot property
          snapshot: snapshotUrl,
        };
      });


      // Sort saved files by creation date (first saved first)
      const savedSorted = [...mappedCanvases].sort(
        (a, b) =>
          new Date(a.createdAt || a.lastModified) -
          new Date(b.createdAt || b.lastModified),
      );

      setSavedStates(savedSorted);

      // Sort recent files by last modified (most recent first)
      const recentSorted = [...mappedCanvases].sort(
        (a, b) => new Date(b.lastModified) - new Date(a.lastModified),
      );
      setRecentFiles(recentSorted);

    } catch (error) {

    }
  };

  const fetchAssets = async () => {
    if (!currentUser) {

      return;
    }

    try {
      // Clear existing assets first
      setAssets([]);
      const assetDocs = await fetchUserAssets(currentUser.uid);

      const validAssets = assetDocs.map((doc) => ({
        ...doc,
        url: doc.url || null,
        modelSnapshot: doc.modelSnapshotPath || null,
        imageSnapshot: doc.imageSnapshotPath || null,
      }));


      setAssets(validAssets);
    } catch (error) {

      setAssets([]); // Ensure empty on error
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchSavedStates();
      fetchAssets();
    }
  }, [currentUser]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".menu-container")) {
        closeAllDropdowns();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Storage usage check
  useEffect(() => {
    async function checkStorageUsage() {
      if (!subscriptionPlan) return;

      try {
        const usageBytes = await fetchUserStorageUsage();
        setCurrentUsageBytes(usageBytes);
        const limitBytes = await getStorageLimitBytes();

        if (usageBytes > limitBytes) {
          setShowStorageLimitModal(true);
        } else {
          setShowStorageLimitModal(false);
        }
      } catch (error) {

      }
    }

    if (subscriptionPlan) {
      checkStorageUsage();
    }
  }, [subscriptionPlan]);

  // Delete handlers

  const handleConfirmDelete = async () => {

    if (!assetToDelete) return;
    try {

      await handleDeleteAsset(assetToDelete);

      setIsDeleteModalOpen(false);
      setAssetToDelete(null);
      setAssetCanvases([]);
    } catch (error) {
    }
  };

  const handleDeleteAsset = async (asset) => {
    try {

      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }
      // Pass the asset type from the asset object
      await deleteAsset(asset.id, currentUser.uid, asset.assetType);

      setAssets((prev) => prev.filter((f) => f.id !== asset.id));
    } catch (error) {
    }
  };

  const handleCanvasDelete = async () => {
    try {

      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }
      await deleteCanvas(canvasToDelete.id, currentUser.uid);

      setSavedStates((prev) =>
        prev.filter((state) => state.id !== canvasToDelete.id),
      );
      setRecentFiles((prev) =>
        prev.filter((file) => file.id !== canvasToDelete.id),
      );
    } catch (error) {

    } finally {
      setIsCanvasDeleteModalOpen(false);
      setCanvasToDelete(null);
    }
  };

  // Asset upload handler - Comprehensive rewrite to fix structural issues
  async function handleAssetUpload(event) {
    if (!currentUser) {

      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const files = Array.from(event.target.files);
      if (!files.length) return;

      // 1. Check storage limits before proceeding
      const currentUsage = await fetchUserStorageUsage();
      const storageLimit = getStorageLimitBytes(subscriptionPlan);
      const totalUploadSize = files.reduce((sum, file) => sum + file.size, 0);

      if (currentUsage + totalUploadSize > storageLimit) {
        setIsUploading(false);
        setShowStorageLimitModal(true);
        setBlockedFileSize(totalUploadSize);
        return;
      }

      // 2. Process each file individually
      const uploadPromises = files.map(async (file) => {
        // Check for duplicates before uploading
        const fileHash = await computeFileHash(file);
        if (assets.some((a) => a.fileHash === fileHash)) {

          return null;
        }

        const lowerName = file.name.toLowerCase();
        const assetType =
          lowerName.endsWith(".glb") ||
          lowerName.endsWith(".gltf") ||
          lowerName.endsWith(".obj")
            ? "model"
            : "image";

        try {
          // 3. Upload the main asset file to get its path
          // The service now handles getting the userId internally.
          const mainFilePath = await uploadFileToStorage(
            file,
            assetType === "model" ? "models" : "images",
          );
          if (!mainFilePath)
            throw new Error("Main file upload failed to return a path.");


          // 4. Generate and upload snapshots/thumbnails
          let modelSnapshotPath = null;
          let imageSnapshotPath = null;

          if (assetType === "model") {
            try {
              const snapshotBlob = await generateModelSnapshot(file);
              const snapshotFile = new File(
                [snapshotBlob],
                `snapshot-${file.name}.webp`,
                { type: "image/webp" },
              );
              modelSnapshotPath = await uploadFileToStorage(
                snapshotFile,
                "thumbnails",
              );

            } catch (snapshotError) {

            }
          } else {
            // assetType is 'image'
            try {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");
              const img = new Image();

              await new Promise((resolve, reject) => {
                img.onload = () => {
                  const maxSize = 400;
                  let { width, height } = img;
                  if (width > height) {
                    if (width > maxSize) {
                      height = (height * maxSize) / width;
                      width = maxSize;
                    }
                  } else {
                    if (height > maxSize) {
                      width = (width * maxSize) / height;
                      height = maxSize;
                    }
                  }
                  canvas.width = width;
                  canvas.height = height;
                  ctx.drawImage(img, 0, 0, width, height);
                  resolve();
                };
                img.onerror = reject;
                img.src = URL.createObjectURL(file);
              });

              const webpBlob = await new Promise((res) =>
                canvas.toBlob(res, "image/webp", 0.8),
              );
              const thumbnailFile = new File(
                [webpBlob],
                `thumbnail-${file.name}.webp`,
                { type: "image/webp" },
              );
              imageSnapshotPath = await uploadFileToStorage(
                thumbnailFile,
                "thumbnails",
              );

            } catch (thumbError) {

            }
          }

          // 5. Create the metadata document for Firestore
          const newAssetId = `asset-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
          const docBody = {
            id: newAssetId,
            assetType,
            name: file.name,
            path: mainFilePath,
            url: mainFilePath, // Use the path for consistency
            uploadedAt: new Date().toISOString(),
            fileHash,
            modelSnapshotPath, // This will be null if not a model
            imageSnapshotPath, // This will be null if not an image
            size: file.size,
          };

          // 6. Call upsertAsset with the CORRECT arguments

          await upsertAsset(currentUser.uid, docBody);


          return docBody;
        } catch (error) {

          return null; // Return null for failed uploads so Promise.all doesn't break
        }
      });

      const results = await Promise.all(uploadPromises);
      const successfullyUploadedAssets = results.filter(Boolean);

      if (successfullyUploadedAssets.length > 0) {

        await fetchAssets(); // Refresh the list from the database
      }
    } catch (error) {

    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Clear the file input
      }
    }
  }

  // Navigation handlers
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {

    }
  };

  const handleNavClick = (section) => {
    setActiveSection(section);
    if (section === "Canvas Presets") {
      setShowNestedList(!showNestedList);
    } else if (section === "Assets") {
      setShowAssetsNestedList(!showAssetsNestedList);
    } else {
      setShowNestedList(false);
      setShowAssetsNestedList(false);
    }
  };

  const createCanvas = () => {
    navigate("/canvas");
  };

  const handleEditProfile = () => {

  };

  // Presets data to match reference image
  const [presets, setPresets] = useState([
    {
      image: "A1.webp",
      title: "A1 Portrait",
      description: "7016 x 9933",
      width: "7016px",
      height: "9933px",
      zoomLevels: { small: 0.067, medium: 0.065, large: 0.06 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A1_Landscape.webp",
      title: "A1 Landscape",
      description: "9933 x 7016",
      width: "9933px",
      height: "7016px",
      zoomLevels: { small: 0.055, medium: 0.075, large: 0.095 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A2.webp",
      title: "A2 Portrait",
      description: "4961 x 7016",
      width: "4961px",
      height: "7016px",
      zoomLevels: { small: 0.095, medium: 0.09, large: 0.09 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A2_Landscape.webp",
      title: "A2 Landscape",
      description: "7016 x 4961",
      width: "7016px",
      height: "4961px",
      zoomLevels: { small: 0.075, medium: 0.11, large: 0.13 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A3.webp",
      title: "A3 Portrait",
      description: "3508 x 4961",
      width: "3508px",
      height: "4961px",
      zoomLevels: { small: 0.13, medium: 0.13, large: 0.132 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A3_Landscape.webp",
      title: "A3 Landscape",
      description: "4961 x 3508",
      width: "4961px",
      height: "3508px",
      zoomLevels: { small: 0.11, medium: 0.15, large: 0.185 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A4.webp",
      title: "A4 Portrait",
      description: "2480 x 3508",
      width: "2480px",
      height: "3508px",
      zoomLevels: { small: 0.19, medium: 0.185, large: 0.185 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A4_Landscape.webp",
      title: "A4 Landscape",
      description: "3508 x 2480",
      width: "3508px",
      height: "2480px",
      zoomLevels: { small: 0.15, medium: 0.22, large: 0.26 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A5.webp",
      title: "A5 Portrait",
      description: "1748 x 2480",
      width: "1748px",
      height: "2480px",
      zoomLevels: { small: 0.3, medium: 0.32, large: 0.33 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A5_Landscape.webp",
      title: "A5 Landscape",
      description: "2480 x 1748",
      width: "2480px",
      height: "1748px",
      zoomLevels: { small: 0.21, medium: 0.3, large: 0.4 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A6.webp",
      title: "A6 Portrait",
      description: "1240 x 1748",
      width: "1240px",
      height: "1748px",
      zoomLevels: { small: 0.41, medium: 0.46, large: 0.47 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A6_Landscape.webp",
      title: "A6 Landscape",
      description: "1748 x 1240",
      width: "1748px",
      height: "1240px",
      zoomLevels: { small: 0.3, medium: 0.43, large: 0.56 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A7.webp",
      title: "A7 Portrait",
      description: "874 x 1240",
      width: "874px",
      height: "1240px",
      zoomLevels: { small: 0.58, medium: 0.65, large: 0.66 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A7_Landscape.webp",
      title: "A7 Landscape",
      description: "1240 x 874",
      width: "1240px",
      height: "874px",
      zoomLevels: { small: 0.43, medium: 0.6, large: 0.8 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A8.webp",
      title: "A8 Portrait",
      description: "614 x 874",
      width: "614px",
      height: "874px",
      zoomLevels: { small: 0.85, medium: 0.93, large: 0.94 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A8_Landscape.webp",
      title: "A8 Landscape",
      description: "874 x 614",
      width: "874px",
      height: "614px",
      zoomLevels: { small: 0.61, medium: 0.85, large: 1.15 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A9.webp",
      title: "A9 Portrait",
      description: "437 x 614",
      width: "437px",
      height: "614px",
      zoomLevels: { small: 1.2, medium: 1.33, large: 1.34 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A9_Landscape.webp",
      title: "A9 Landscape",
      description: "614 x 437",
      width: "614px",
      height: "437px",
      zoomLevels: { small: 0.85, medium: 1.25, large: 1.65 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A10.webp",
      title: "A10 Portrait",
      description: "307 x 437",
      width: "307px",
      height: "437px",
      zoomLevels: { small: 1.7, medium: 1.85, large: 1.86 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "A10_Landscape.webp",
      title: "A10 Landscape",
      description: "437 x 307",
      width: "437px",
      height: "307px",
      zoomLevels: { small: 1.2, medium: 1.7, large: 2.3 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "Business_card.webp",
      title: "Business Card",
      description: "1050 x 600",
      width: "1050px",
      height: "600px",
      zoomLevels: { small: 0.51, medium: 0.72, large: 0.95 },
      zoom: 0,
      category: "Print",
    },
    {
      image: "Facebook_post_landscape.webp",
      title: "Facebook Post Landscape",
      description: "1200 x 630",
      width: "1200px",
      height: "630px",
      zoomLevels: { small: 0.45, medium: 0.62, large: 0.85 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Facebook_cover.webp",
      title: "Facebook Cover",
      description: "851 x 315",
      width: "851px",
      height: "315px",
      zoomLevels: { small: 0.63, medium: 0.9, large: 1.2 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Instagram_Post_Landscape.webp",
      title: "Instagram Post Landscape",
      description: "1080 x 566",
      width: "1080px",
      height: "566px",
      zoomLevels: { small: 0.5, medium: 0.7, large: 0.95 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Instagram_Post_Portrait.webp",
      title: "Instagram Post Portrait",
      description: "1080 x 1350",
      width: "1080px",
      height: "1350px",
      zoomLevels: { small: 0.5, medium: 0.6, large: 0.61 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Instagram_Story.webp",
      title: "Instagram Story",
      description: "1080 x 1920",
      width: "1080px",
      height: "1920px",
      zoomLevels: { small: 0.34, medium: 0.34, large: 0.34 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Linkedin_Banner.webp",
      title: "LinkedIn Banner",
      description: "1584 x 396",
      width: "1584px",
      height: "396px",
      zoomLevels: { small: 0.34, medium: 0.48, large: 0.65 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Linkedin_Post_Landscape.webp",
      title: "LinkedIn Post Landscape",
      description: "1200 x 627",
      width: "1200px",
      height: "627px",
      zoomLevels: { small: 0.45, medium: 0.64, large: 0.84 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Pintrest_Post.webp",
      title: "Pinterest Post",
      description: "1000 x 1500",
      width: "1000px",
      height: "1500px",
      zoomLevels: { small: 0.43, medium: 0.43, large: 0.43 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Powerpoint_4_3.webp",
      title: "PowerPoint Slide",
      description: "1024 x 768 (4 : 3)",
      width: "1024px",
      height: "768px",
      zoomLevels: { small: 0.53, medium: 0.74, large: 0.85 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Powerpoint_16_9.webp",
      title: "PowerPoint Slide",
      description: "1920 x 1080 (16 : 9)",
      width: "1920px",
      height: "1080px",
      zoomLevels: { small: 0.28, medium: 0.4, large: 0.53 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Social_Post_Sqaure.webp",
      title: "Social Post Square",
      description: "1080 x 1080",
      width: "1080px",
      height: "1080px",
      zoomLevels: { small: 0.5, medium: 0.7, large: 0.75 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "X_Post_Landscape.webp",
      title: "X Post Landscape",
      description: "1200 x 675",
      width: "1200px",
      height: "675px",
      zoomLevels: { small: 0.45, medium: 0.63, large: 0.85 },
      zoom: 0,
      category: "Digital",
    },
    {
      image: "Youtube_Thumbnail.webp",
      title: "YouTube Thumbnail",
      description: "1280 x 720",
      width: "1280px",
      height: "720px",
      zoomLevels: { small: 0.43, medium: 0.6, large: 0.8 },
      zoom: 0,
      category: "Digital",
    },
  ]);

  // Recalc zoom
  const calculateZoomForPreset = (preset) => {
    if (window.matchMedia("(max-width: 1439px)").matches) {
      return preset.zoomLevels.small;
    } else if (window.matchMedia("(max-width: 1919px)").matches) {
      return preset.zoomLevels.medium;
    } else {
      return preset.zoomLevels.large;
    }
  };

  const updatePresetZooms = () => {
    const updatedPresets = presets.map((preset) => ({
      ...preset,
      zoom: calculateZoomForPreset(preset),
    }));
    setPresets(updatedPresets);
  };

  useEffect(() => {
    updatePresetZooms();
    window.addEventListener("resize", updatePresetZooms);
    return () => window.removeEventListener("resize", updatePresetZooms);
  }, []);

  return (
    <div className="dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="logo-container-dashboard">
            <img
              src="/You_Create_Logo_WEB.png"
              alt="YouCreate Logo"
              className="logo"
            />
          </div>

          <div className="header-greeting">
            <div className="welcome-text">
              Welcome back, {userName || "User"}!
            </div>
            {isTrialSubscription && trialDaysRemaining !== null && (
              <div className="trial-indicator">
                Trial: {trialDaysRemaining} days remaining
              </div>
            )}
            <button className="create-button" onClick={createCanvas}>
              <span className="gradient-text">Start Creating</span>
            </button>
          </div>

          <div style={{ position: "relative" }}>
            <button className="header-account-toggle" onClick={toggleDropdown}>
              <span className="gradient-text">
                {userName ? userName.charAt(0).toUpperCase() : "U"}
              </span>
            </button>
            {dropdownOpen && (
              <ul className="dropdown-menu">
                <li onClick={() => setIsAccountModalOpen(true)}>Account</li>
                <li onClick={handleLogout}>Logout</li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main-content">
        {/* Navigation Bar */}
        <div className="dashboard-navbar">
          <ul>
            <li
              className={activeSection === "Recent Files" ? "selected" : ""}
              onClick={() => handleNavClick("Recent Files")}
            >
              Recent Files
            </li>
            <li
              className={activeSection === "Saved Files" ? "selected" : ""}
              onClick={() => handleNavClick("Saved Files")}
            >
              Saved Files
            </li>
            <hr className="dashboard-navbar-lb" />
            <li
              className={activeSection === "Canvas Presets" ? "selected" : ""}
              onClick={() => handleNavClick("Canvas Presets")}
            >
              Canvas Presets
            </li>
            {showNestedList && (
              <ul className="nested-list">
                <li
                  className={
                    activeSection === "Digital Presets" ? "selected" : ""
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSection("Digital Presets");
                    setShowNestedList(true);
                  }}
                >
                  Digital
                </li>
                <li
                  className={
                    activeSection === "Print Presets" ? "selected" : ""
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSection("Print Presets");
                    setShowNestedList(true);
                  }}
                >
                  Print
                </li>
              </ul>
            )}
            <hr className="dashboard-navbar-lb" />
            <li
              className={activeSection === "Assets" ? "selected" : ""}
              onClick={() => handleNavClick("Assets")}
            >
              Assets
            </li>
            {showAssetsNestedList && (
              <ul className="nested-list">
                <li
                  className={activeSection === "3D Models" ? "selected" : ""}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSection("3D Models");
                    setShowAssetsNestedList(true);
                  }}
                >
                  3D Models
                </li>
                <li
                  className={activeSection === "Image Files" ? "selected" : ""}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSection("Image Files");
                    setShowAssetsNestedList(true);
                  }}
                >
                  Images
                </li>
              </ul>
            )}
            <hr className="dashboard-navbar-lb" />
            <li
              onClick={() =>
                window.open("https://youcreate.app/how-to/", "_blank")
              }
            >
              <a href="#" target="_blank">
                How-to
              </a>
            </li>
            <li onClick={() => window.open("https://youcreate.app/", "_blank")}>
              <a href="https://youcreate.app/" target="_blank">
                Learn More
              </a>
            </li>
            <hr className="dashboard-navbar-lb" />
          </ul>
        </div>

        {/* Content Display Area */}
        <div className="canvas-display-container">
          {activeSection === "Recent Files" && (
            <div>
              <div className="recent-files-header">
                <div className="recent-files-header-left">
                  <h1>Recent Files</h1>
                  {isMultiSelectMode && selectedCanvases.length > 0 && (
                    <button
                      onClick={handleBulkDelete}
                      style={{
                        backgroundColor: "#dc3545",
                        color: "white",
                      }}
                    >
                      Delete Selected ({selectedCanvases.length})
                    </button>
                  )}
                  {isMultiSelectMode && (
                    <button
                      onClick={() => {
                        setIsMultiSelectMode(false);
                        setSelectedCanvases([]);
                      }}
                      style={{
                        backgroundColor: "#6c757d",
                        color: "white",
                      }}
                    >
                      Cancel Selection
                    </button>
                  )}
                </div>
                <div className="recent-files-header-right">
                  <input
                    type="text"
                    className="search-bar"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="recent-files-grid">
                {recentFiles.length === 0 ? (
                  <p>No recent files found.</p>
                ) : (
                  recentFiles
                    .filter((file) =>
                      (file.name || file.canvasName || "Untitled")
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                    )
                    .map((file) => (
                      <div key={file.id} className="file-card">
                        {isMultiSelectMode && (
                          <input
                            type="checkbox"
                            checked={selectedCanvases.includes(file.id)}
                            onChange={() => toggleSelection(file.id, "canvas")}
                          />
                        )}
                        <button
                          className="file-button"
                          onClick={() => navigate("/canvas", { state: file })}
                          style={{
                            backgroundImage: file.snapshot
                              ? `url(${file.snapshot})`
                              : "none",
                          }}
                        >
                          {!file.snapshot && <span>Open Canvas</span>}
                        </button>
                        <div className="file-card-header">
                          {editingName === `recent_${file.id}` ? (
                            <input
                              className="canvas-name-input"
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onBlur={() =>
                                handleRenameSubmit(file.id, editingValue)
                              }
                              onKeyDown={async (e) => {
                                if (e.key === "Enter") {
                                  await handleRenameSubmit(
                                    file.id,
                                    editingValue,
                                  );
                                  e.target.blur();
                                }
                                if (e.key === "Escape") {
                                  setEditingName(null);
                                  setEditingValue("");
                                  e.target.blur();
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <div className="canvas-name-display">
                              {file.name || file.canvasName || "Untitled"}
                            </div>
                          )}
                          <div className="menu-container">
                            <button
                              className="menu-toggle"
                              onClick={() =>
                                toggleDropdownMenu(file.id, "recent")
                              }
                            >
                              ☰
                            </button>
                            {openDropdowns[`recent_${file.id}`] && (
                              <div className="dropdown-menu file-dropdown">
                                <ul>
                                  <li
                                    onClick={() => {
                                      closeAllDropdowns();
                                      startEditing(
                                        `recent_${file.id}`,
                                        file.name ||
                                          file.canvasName ||
                                          "Untitled",
                                      );
                                      setTimeout(() => {
                                        const input = document.querySelector(
                                          `input[value="${file.name || file.canvasName || "Untitled"}"]`,
                                        );
                                        if (input) input.focus();
                                      }, 10);
                                    }}
                                  >
                                    Rename
                                  </li>
                                  <li
                                    onClick={() => handleDuplicateCanvas(file)}
                                  >
                                    Duplicate
                                  </li>
                                  <li
                                    onClick={() => {
                                      setIsMultiSelectMode(true);
                                      toggleSelection(file.id, "canvas");
                                      closeAllDropdowns();
                                    }}
                                  >
                                    Select
                                  </li>
                                  <li
                                    onClick={() =>
                                      handleCanvasDeleteDialog(file)
                                    }
                                  >
                                    Delete
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                        <p>
                          Last modified:{" "}
                          {new Date(file.lastModified).toLocaleDateString()}
                        </p>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}

          {activeSection === "Saved Files" && (
            <div>
              <div className="saved-files-header">
                <div className="recent-files-header-left">
                  <h1>Saved Files</h1>
                  {isMultiSelectMode && selectedCanvases.length > 0 && (
                    <button
                      onClick={handleBulkDelete}
                      style={{
                        backgroundColor: "#dc3545",
                        color: "white",
                      }}
                    >
                      Delete Selected ({selectedCanvases.length})
                    </button>
                  )}
                  {isMultiSelectMode && (
                    <button
                      onClick={() => {
                        setIsMultiSelectMode(false);
                        setSelectedCanvases([]);
                      }}
                      style={{
                        backgroundColor: "#6c757d",
                        color: "white",
                      }}
                    >
                      Cancel Selection
                    </button>
                  )}
                </div>
                <div className="recent-files-header-right">
                  <input
                    type="text"
                    className="search-bar"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="saved-files-grid">
                {savedStates.length === 0 ? (
                  <p>No saved files found.</p>
                ) : (
                  savedStates
                    .filter((file) =>
                      (file.name || file.canvasName || "Untitled")
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                    )
                    .map((file) => (
                      <div key={file.id} className="file-card">
                        {isMultiSelectMode && (
                          <input
                            type="checkbox"
                            checked={selectedCanvases.includes(file.id)}
                            onChange={() => toggleSelection(file.id, "canvas")}
                          />
                        )}
                        <button
                          className="file-button"
                          onClick={() => navigate("/canvas", { state: file })}
                          style={{
                            backgroundImage: file.snapshot
                              ? `url(${file.snapshot})`
                              : "none",
                          }}
                        >
                          {!file.snapshot && <span>Open Canvas</span>}
                        </button>
                        <div className="file-card-header">
                          {editingName === `saved_${file.id}` ? (
                            <input
                              className="canvas-name-input"
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onBlur={() =>
                                handleRenameSubmit(file.id, editingValue)
                              }
                              onKeyDown={async (e) => {
                                if (e.key === "Enter") {
                                  await handleRenameSubmit(
                                    file.id,
                                    editingValue,
                                  );
                                  e.target.blur();
                                }
                                if (e.key === "Escape") {
                                  setEditingName(null);
                                  setEditingValue("");
                                  e.target.blur();
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <div className="canvas-name-display">
                              {file.name || file.canvasName || "Untitled"}
                            </div>
                          )}
                          <div className="menu-container">
                            <button
                              className="menu-toggle"
                              onClick={() =>
                                toggleDropdownMenu(file.id, "saved")
                              }
                            >
                              ☰
                            </button>
                            {openDropdowns[`saved_${file.id}`] && (
                              <div className="dropdown-menu file-dropdown">
                                <ul>
                                  <li
                                    onClick={() => {
                                      closeAllDropdowns();
                                      startEditing(
                                        `saved_${file.id}`,
                                        file.name ||
                                          file.canvasName ||
                                          "Untitled",
                                      );
                                      setTimeout(() => {
                                        const input = document.querySelector(
                                          `input[value="${file.name || file.canvasName || "Untitled"}"]`,
                                        );
                                        if (input) input.focus();
                                      }, 10);
                                    }}
                                  >
                                    Rename
                                  </li>
                                  <li
                                    onClick={() => handleDuplicateCanvas(file)}
                                  >
                                    Duplicate
                                  </li>
                                  <li
                                    onClick={() => {
                                      setIsMultiSelectMode(true);
                                      toggleSelection(file.id, "canvas");
                                      closeAllDropdowns();
                                    }}
                                  >
                                    Select
                                  </li>
                                  <li
                                    onClick={() =>
                                      handleCanvasDeleteDialog(file)
                                    }
                                  >
                                    Delete
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                        <p>
                          Last modified:{" "}
                          {new Date(file.lastModified).toLocaleDateString()}
                        </p>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}

          {(activeSection === "Assets" ||
            activeSection === "3D Models" ||
            activeSection === "Image Files") && (
            <div>
              <div className="recent-files-header">
                <div className="recent-files-header-left">
                  <h1>{activeSection}</h1>
                  <button onClick={() => fileInputRef.current?.click()}>
                    Upload Assets
                  </button>
                  {isUploading && (
                    <div className="upload-status">
                      <span>Uploading... {uploadProgress}%</span>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {isMultiSelectMode && selectedAssets.length > 0 && (
                    <button
                      onClick={handleBulkDelete}
                      style={{
                        backgroundColor: "#dc3545",
                        color: "white",
                      }}
                    >
                      Delete Selected ({selectedAssets.length})
                    </button>
                  )}
                  {isMultiSelectMode && (
                    <button
                      onClick={() => {
                        setIsMultiSelectMode(false);
                        setSelectedAssets([]);
                      }}
                      style={{
                        backgroundColor: "#6c757d",
                        color: "white",
                      }}
                    >
                      Cancel Selection
                    </button>
                  )}
                </div>
                <div className="recent-files-header-right">
                  <input
                    type="text"
                    className="search-bar"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="assets-grid">
                {assets.length === 0 ? (
                  <p>No assets uploaded yet.</p>
                ) : (
                  assets
                    .filter((asset) => {
                      const matchesSearch = asset.name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase());

                      if (activeSection === "3D Models") {
                        return (
                          matchesSearch &&
                          asset.path &&
                          (asset.path.endsWith(".glb") ||
                            asset.path.endsWith(".obj") ||
                            asset.path.endsWith(".fbx") ||
                            asset.assetType === "model")
                        );
                      } else if (activeSection === "Image Files") {
                        return (
                          matchesSearch &&
                          (asset.assetType === "image" ||
                            (asset.path &&
                              (asset.path.toLowerCase().includes(".png") ||
                                asset.path.toLowerCase().includes(".jpg") ||
                                asset.path.toLowerCase().includes(".jpeg") ||
                                asset.path.toLowerCase().includes(".gif") ||
                                asset.path.toLowerCase().includes(".webp"))))
                        );
                      } else {
                        return matchesSearch;
                      }
                    })
                    .map((asset) => (
                      <div key={asset.id} className="file-card">
                        {isMultiSelectMode && (
                          <input
                            type="checkbox"
                            checked={selectedAssets.includes(asset.id)}
                            onChange={() => toggleSelection(asset.id, "asset")}
                          />
                        )}
                        <button
                          className="file-button"
                          onClick={() => {
                            // Handle asset preview/open
                            if (asset.assetType === "image") {
                              window.open(asset.url, "_blank");
                            }
                          }}
                          style={{
                            backgroundImage: asset.thumbnailUrl
                              ? `url(${asset.thumbnailUrl})`
                              : "none",
                            backgroundColor: asset.thumbnailUrl
                              ? "transparent"
                              : "#f0f0f0",
                          }}
                        >
                          {asset.assetType === "model" &&
                            !asset.thumbnailUrl && (
                              <span style={{ color: "#666", fontSize: "14px" }}>
                                3D Model
                              </span>
                            )}
                        </button>
                        <div className="file-card-header">
                          {editingName === `asset_${asset.id}` ? (
                            <input
                              className="canvas-name-input"
                              type="text"
                              value={asset.name}
                              onChange={(e) => {
                                const newName = e.target.value;
                                setAssets((prev) =>
                                  prev.map((a) =>
                                    a.id === asset.id
                                      ? { ...a, name: newName }
                                      : a,
                                  ),
                                );
                              }}
                              onBlur={async (e) => {
                                const newName = e.target.value;
                                try {
                                  await renameAsset(
                                    asset.id,
                                    newName,
                                    asset.assetType,
                                  );

                                } catch (error) {

                                }
                                setEditingName(null);
                              }}
                              onKeyDown={async (e) => {
                                if (e.key === "Enter") {
                                  const newName = e.target.value;
                                  try {
                                    await renameAsset(
                                      asset.id,
                                      newName,
                                      asset.assetType,
                                    );

                                  } catch (error) {

                                  }
                                  setEditingName(null);
                                  e.target.blur();
                                }
                                if (e.key === "Escape") {
                                  setEditingName(null);
                                  e.target.blur();
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <div className="canvas-name-display">
                              {truncateName(asset.name)}
                            </div>
                          )}
                          <div className="menu-container">
                            <button
                              className="menu-toggle"
                              onClick={() =>
                                toggleDropdownMenu(asset.id, "asset")
                              }
                            >
                              ☰
                            </button>
                            {openDropdowns[`asset_${asset.id}`] && (
                              <div className="dropdown-menu file-dropdown">
                                <ul>
                                  <li
                                    onClick={() => {
                                      closeAllDropdowns();
                                      setEditingName(`asset_${asset.id}`);
                                    }}
                                  >
                                    Rename
                                  </li>
                                  <li
                                    onClick={() => {
                                      setIsMultiSelectMode(true);
                                      toggleSelection(asset.id, "asset");
                                      closeAllDropdowns();
                                    }}
                                  >
                                    Select
                                  </li>
                                  <li
                                    onClick={() =>
                                      handleAssetDeleteDialog(asset)
                                    }
                                  >
                                    Delete
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                        <p>
                          Uploaded:{" "}
                          {new Date(asset.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}

          {(activeSection === "Canvas Presets" ||
            activeSection === "Digital Presets" ||
            activeSection === "Print Presets") && (
            <div>
              <div className="canvas-presets-header">
                <h1>{activeSection}</h1>
                <div className="canvas-presets-header-right">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="presets-grid">
                {presets
                  .filter((preset) => {
                    if (activeSection === "Digital Presets") {
                      return (
                        preset.category === "Digital" &&
                        preset.title
                          ?.toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      );
                    } else if (activeSection === "Print Presets") {
                      return (
                        preset.category === "Print" &&
                        preset.title
                          ?.toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      );
                    } else {
                      return preset.title
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase());
                    }
                  })
                  .map((preset, index) => (
                    <div key={index} className="file-card">
                      <button
                        className="file-button"
                        onClick={() =>
                          navigate("/canvas", {
                            state: {
                              preset: preset,
                              width: preset.width,
                              height: preset.height,
                            },
                          })
                        }
                      >
                        <div className="preset-icon">
                          <img
                            src={`/${preset.image}`}
                            alt={preset.title}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        </div>
                      </button>
                      <div className="file-card-header">
                        <h3 className="canvas-name-text">{preset.title}</h3>
                      </div>
                      <p>{preset.description}</p>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isDeleteModalOpen && (
        <div className="modal-overlay-dashboard">
          <div className="modal-content-dashboard">
            <h3>Delete Asset</h3>
            <p>Are you sure you want to delete "{assetToDelete?.name}"?</p>

            {assetCanvases.length > 0 && (
              <div>
                <p>This asset is used in {assetCanvases.length} canvas(es):</p>

                <ul>
                  {assetCanvases.map((canvas) => (
                    <li key={canvas.id}>{canvas.name || canvas.canvasName || canvas.title || `Canvas ${canvas.id}`}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="modal-buttons">
              <button
                onClick={() => {

                  handleConfirmDelete();
                }}
              >
                Delete
              </button>
              <button onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isBulkDeleteModalOpen && (
        <div className="modal-overlay-dashboard">
          <div className="modal-content-dashboard">
            <h3>Delete Selected Items</h3>
            <p>
              Are you sure you want to delete {selectedCanvases.length}{" "}
              canvas(es) and {selectedAssets.length} asset(s)?
            </p>
            <div className="modal-buttons">
              <button onClick={confirmBulkDelete}>Delete</button>
              <button onClick={() => setIsBulkDeleteModalOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isCanvasDeleteModalOpen && (
        <div className="modal-overlay-dashboard">
          <div className="modal-content-dashboard">
            <h3>Delete Canvas</h3>
            <p>Are you sure you want to delete "{canvasToDelete?.name}"?</p>

            <div className="modal-buttons">
              <button
                onClick={() => {

                  handleCanvasDelete();
                }}
              >
                Delete
              </button>
              <button onClick={() => setIsCanvasDeleteModalOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showStorageLimitModal && (
        <div className="modal-overlay-dashboard">
          <div className="modal-content-dashboard">
            <h2>Storage Limit Exceeded</h2>
            <p>Your current storage limit is {(100).toFixed(0)} KB.</p>
            {blockedFileSize && (
              <p>
                The file(s) you attempted to upload total{" "}
                {(blockedFileSize / 1000).toFixed(2)} KB.
              </p>
            )}
            <p>
              Please delete some files to free up space or upgrade your plan.
            </p>
            <div>
              <button onClick={() => setShowStorageLimitModal(false)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {showCanvasLimitModal && (
        <div className="modal-overlay-dashboard">
          <div className="modal-content-dashboard">
            <h2>Canvas Limit Exceeded</h2>
            <p>
              Your subscription plan has reached its saved canvas limit. Please
              delete an existing canvas or upgrade your plan.
            </p>
            <div>
              <button onClick={() => setShowCanvasLimitModal(false)}>
                Dismiss
              </button>
              <button onClick={() => navigate("/upgrade")}>Upgrade Plan</button>
            </div>
          </div>
        </div>
      )}

      {/* Account Modal */}
      {isAccountModalOpen && (
        <div className="account-modal-overlay">
          <div className="account-modal">
            {/* Header */}
            <div className="account-modal-header">
              <h2>Account Settings</h2>
              <button
                className="close-button-account"
                onClick={() => {
                  setIsAccountModalOpen(false);
                  setIsEditingName(false); // Close editing mode when modal closes
                }}
              >
                ×
              </button>
            </div>

            {/* Profile Section */}
            <div className="account-profile-section">
              <div className="profile-avatar">
                <div className="avatar-circle">
                  {userName ? userName.charAt(0).toUpperCase() : "U"}
                </div>
              </div>
              <div className="profile-info">
                <h3 className="profile-name">{userName || "User"}</h3>
                <p className="profile-email">{userEmail}</p>
              </div>
            </div>

            {/* Account Details */}
            <div className="account-details">
              <div className="account-section">
                <h4>Personal Information</h4>
                <div className="info-row">
                  <span className="info-label">Name</span>
                  {isEditingName ? (
                    <div className="value-editing">
                      <input 
                        type="text"
                        value={newUserName}
                        onChange={(e) => setNewUserName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleNameChangeSubmit()}
                        autoFocus
                        className="name-input"
                      />
                      <button onClick={handleNameChangeSubmit} className="save-button">Save</button>
                      <button onClick={() => setIsEditingName(false)} className="cancel-button">Cancel</button>
                    </div>
                  ) : (
                    <div className="info-value-group">
                      <span className="info-value">{userName}</span>
                      <button className="edit-button" onClick={handleEditName}>Edit</button>
                    </div>
                  )}
                </div>
                {nameUpdateMessage && <div className="success-message" style={{marginTop: '10px'}}>{nameUpdateMessage}</div>}
                <div className="info-row">
                  <span className="info-label">Email</span>
                  <div className="info-value-group">
                    <span className="info-value">{userEmail}</span>
                    <button className="edit-button" onClick={handleEditProfile}>Edit</button>
                  </div>
                </div>
              </div>

              <div className="account-section">
                <h4>Subscription</h4>
                <div className="subscription-card">
                  <div className="subscription-header">
                    <h5>Upgrade Plan & Purchase Tokens</h5>
                    <p>Unlock premium features and get more AI tokens</p>
                  </div>
                  <button 
                    className="primary-button" 
                    onClick={() => {}}
                  >
                    Upgrade Plan
                  </button>
                </div>

                <div className="subscription-card">
                  <div className="subscription-header">
                    <h5>Manage Subscription</h5>
                    <p>Update payment methods and billing information</p>
                  </div>
                  <button 
                    className="secondary-button" 
                    onClick={() => {}}
                  >
                    Manage Billing
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="account-modal-footer">
              <button className="logout-button" onClick={handleLogout}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for 3D model screenshots */}
      <canvas
        ref={hiddenCanvasRef}
        style={{ display: "none" }}
        width={512}
        height={512}
      />

      {/* Hidden file input for asset uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.glb,.gltf,.fbx,.obj"
        multiple
        style={{ display: "none" }}
        onChange={handleAssetUpload}
      />
    </div>
  );
};

export default Dashboard;
